// The resolution book (docs/architecture/BRE-Resolution-Book-Plan.md; ADR
// 0048). Builds on the register of ADR 0029 §4: which motions are numbered
// (§3 of the plan), the board's and the Executive Committee's series by
// fiscal year, the links by which a later resolution amends, rescinds,
// supersedes or ratifies an earlier one, the status derived from those
// links (the original record never changes), and the historical import from
// the old minutes (§6).
//
// The pure rules here are what Platform Core decides with; the staff UI and
// myBRE only explain them.

import { z } from 'zod'
import type { SheetAttendanceNote } from './board-attendance-records'
import type { MeetingBody, PersonRef } from './governance'
import { governanceFiscalYear } from './governance'
import type { VoteTally } from './governance-decisions'

// ------------------------------------------------------------ motion kinds

// Plan §3. A decision is numbered; a routine approval (the agenda, the
// minutes, the monthly financials) and a procedural motion (adjourn,
// recess, enter or leave executive session, lay on or take from the table)
// are recorded in the minutes and never numbered.
export const REGISTER_KINDS = ['decision', 'procedural', 'routine_approval'] as const
export type RegisterKind = (typeof REGISTER_KINDS)[number]
export const REGISTER_KIND_LABELS: Record<RegisterKind, string> = {
  decision: 'Decision (numbered resolution)',
  procedural: 'Procedural (recorded, not numbered)',
  routine_approval: 'Routine approval (recorded, not numbered)',
}
export const REGISTER_KIND_SHORT: Record<RegisterKind, string> = { decision: 'Decision', procedural: 'Procedural', routine_approval: 'Routine approval' }

const PROCEDURAL_TITLES = [/^(motion to |move to )?adjourn/i, /\brecess\b/i, /\b(enter|go into|move into|come out of|leave|exit|return from)\b.*\bexecutive session\b/i, /\b(lay|laid)\b.*\bon the table\b/i, /\btake from the table\b/i, /^(motion to )?table\b/i]
// Routine only when the title is plainly approving the agenda, the minutes,
// the financials or treasurer's report, or the consent agenda: the verb
// leads, at most a few words (a date, "September", "revised") come before
// the thing approved, and nothing else is the object. "Approve the Carter
// Roofing contract" and "Approve the clear bag policy" are decisions.
const LEAD = String.raw`^(?:(?:motion|move|moved)(?:,)? (?:to |that the board )?)?`
const FEW = String.raw`(?:the\s+)?(?:[\w’'.,-]+\s+){0,3}?`
const TAIL = String.raw`(?:\s+(?:of|for|from|dated|as)\b.*|[.,;:]?\s*)$`
const ROUTINE_TITLES = [
  new RegExp(`${LEAD}(?:approve|adopt|accept)s?\\s+${FEW}(?:consent\\s+)?agenda${TAIL}`, 'i'),
  new RegExp(`${LEAD}(?:approve|accept|adopt)s?\\s+${FEW}minutes${TAIL}`, 'i'),
  new RegExp(`${LEAD}(?:approve|accept|receive)s?\\s+${FEW}(?:financials?|financial (?:reports?|statements?)|treasurer['’]?s report)${TAIL}`, 'i'),
]

// The Treasurer's report or the financials, plainly (owner, 2026-10-26:
// never a consent item): "Treasurer's report", "Financial report",
// "September financials", "Approve the financial statements for August",
// "Report of the Treasurer". "Financial audit RFP" and "Finance Committee
// report" are not.
const FINANCIAL_TITLE = new RegExp(`${LEAD}(?:(?:approve|accept|receive|review|adopt)s?\\s+)?${FEW}(?:financials?|financial (?:reports?|statements?)|treasurer['’]?s? reports?|report of the treasurer)${TAIL}`, 'i')
export const isFinancialReportTitle = (title: string) => FINANCIAL_TITLE.test(title.trim())

// The default kind, from what the motion is. The ED changes it in the agenda
// builder or the conductor before the vote opens; from then on it is fixed.
export function defaultRegisterKind(m: { title: string; minutesId?: string | null; disposesMotionId?: string | null; consentItems?: { minutesId: string | null; itemType?: string | null; autoSource?: string | null }[] | null }): RegisterKind {
  if (m.disposesMotionId) return 'procedural'
  if (m.minutesId) return 'routine_approval'
  if (m.consentItems) return m.consentItems.every((i) => i.minutesId || i.autoSource === 'agenda_approval' || ['executive_committee_report', 'financial_report', 'report', 'committee_report', 'email_meeting_report', 'action_items_report'].includes(i.itemType ?? '')) ? 'routine_approval' : 'decision'
  if (PROCEDURAL_TITLES.some((r) => r.test(m.title))) return 'procedural'
  if (ROUTINE_TITLES.some((r) => r.test(m.title))) return 'routine_approval'
  return 'decision'
}

// ------------------------------------------------------------- numbering

// Owner decisions 1 and 2: the board's series R-, the Executive Committee's
// EC-, each within the fiscal year (July 1 to June 30). Historical
// resolutions entered from records before Hub carry H-; those adopted in
// FY2027 (the year Hub's register began) go into R-2027 and EC-2027 so the
// current year is one series (plan §6).
export const RESOLUTION_SERIES = ['R', 'EC', 'H-R', 'H-EC'] as const
export type ResolutionSeries = (typeof RESOLUTION_SERIES)[number]
export const FIRST_HUB_FISCAL_YEAR = 2027

export function seriesFor(body: 'board' | 'executive', fiscalYear: number, origin: 'hub' | 'records'): ResolutionSeries {
  const base = body === 'executive' ? 'EC' : 'R'
  return origin === 'records' && fiscalYear < FIRST_HUB_FISCAL_YEAR ? (`H-${base}` as ResolutionSeries) : base
}

export const formatResolutionNumber = (series: ResolutionSeries, fiscalYear: number, seq: number) => `${series}-${fiscalYear}-${String(seq).padStart(3, '0')}`

export const RESOLUTION_BODY_LABELS: Record<'board' | 'executive', string> = { board: 'Board of Directors', executive: 'Executive Committee' }

// --------------------------------------------------------------- links

// A later resolution names the earlier one it acts on. Adoption makes the
// link; the earlier record never changes (its status is derived).
export const RESOLUTION_LINK_EFFECTS = ['amends', 'rescinds', 'supersedes', 'ratifies'] as const
export type ResolutionLinkEffect = (typeof RESOLUTION_LINK_EFFECTS)[number]
export const LINK_EFFECT_LABELS: Record<ResolutionLinkEffect, string> = { amends: 'Amends', rescinds: 'Rescinds', supersedes: 'Supersedes', ratifies: 'Ratifies' }
export const LINK_EFFECT_PAST: Record<ResolutionLinkEffect, string> = { amends: 'Amended by', rescinds: 'Rescinded by', supersedes: 'Superseded by', ratifies: 'Ratified by' }

// What a motion may name before it is voted on: an earlier resolution
// (amend, rescind, supersede, ratify; or, for the board, accept an
// Executive Committee report, which is recorded, not numbered), or a policy
// (adopt, amend, retire).
export const MOTION_LINK_EFFECTS = [...RESOLUTION_LINK_EFFECTS, 'accepts', 'adopts_policy', 'amends_policy', 'retires_policy'] as const
export type MotionLinkEffect = (typeof MOTION_LINK_EFFECTS)[number]
export const MOTION_LINK_LABELS: Record<MotionLinkEffect, string> = {
  amends: 'Amends',
  rescinds: 'Rescinds',
  supersedes: 'Supersedes',
  ratifies: 'Ratifies',
  accepts: 'Accepts the report of',
  adopts_policy: 'Adopts the policy',
  amends_policy: 'Amends the policy',
  retires_policy: 'Retires the policy',
}
export const POLICY_EFFECTS = ['adopted', 'amended', 'retired'] as const
export type PolicyEffect = (typeof POLICY_EFFECTS)[number]
export const POLICY_EFFECT_FROM_MOTION: Partial<Record<MotionLinkEffect, PolicyEffect>> = { adopts_policy: 'adopted', amends_policy: 'amended', retires_policy: 'retired' }

// --------------------------------------------------------------- status

export const RESOLUTION_STATUSES = ['in_effect', 'amended', 'rescinded', 'superseded', 'expired'] as const
export type ResolutionStatus = (typeof RESOLUTION_STATUSES)[number]
export const RESOLUTION_STATUS_LABELS: Record<ResolutionStatus, string> = { in_effect: 'In effect', amended: 'Amended', rescinded: 'Rescinded', superseded: 'Superseded', expired: 'Expired' }
// "In effect" (the default filter) includes a resolution amended but not
// rescinded: it still stands, as amended.
export const IN_EFFECT_STATUSES: ResolutionStatus[] = ['in_effect', 'amended']

export type StatusInput = { id: string; number: string; expiresOn: string | null }
export type LinkInput = { id: string; resolutionId: string; targetResolutionId: string; effect: ResolutionLinkEffect; retracted?: boolean }
export type DerivedStatus = {
  status: ResolutionStatus
  // The live links that make the status (amended by R-…, ratified by R-…).
  by: { effect: ResolutionLinkEffect; resolutionId: string; number: string }[]
  // Links that no longer count, because the resolution that made them was
  // itself rescinded or superseded (an amendment later rescinded).
  lapsed: { effect: ResolutionLinkEffect; resolutionId: string; number: string }[]
}

// Derives every resolution's status from the link rows alone. A link counts
// only while the resolution that made it is not itself rescinded or
// superseded: rescinding an amendment restores the original as it stood.
// Rescinded beats superseded beats expired beats amended. Cycles (which
// Hub refuses to create) are broken by treating a resolution under
// evaluation as standing.
export function deriveStatuses(resolutions: StatusInput[], links: LinkInput[], today: string): Map<string, DerivedStatus> {
  const byId = new Map(resolutions.map((r) => [r.id, r]))
  const incoming = new Map<string, LinkInput[]>()
  for (const l of links) if (!l.retracted && byId.has(l.resolutionId) && byId.has(l.targetResolutionId)) incoming.set(l.targetResolutionId, [...(incoming.get(l.targetResolutionId) ?? []), l])
  const memo = new Map<string, DerivedStatus>()
  const visiting = new Set<string>()
  const standing = (id: string): boolean => {
    const s = evaluate(id).status
    return s !== 'rescinded' && s !== 'superseded'
  }
  function evaluate(id: string): DerivedStatus {
    const done = memo.get(id)
    if (done) return done
    if (visiting.has(id)) return { status: 'in_effect', by: [], lapsed: [] }
    visiting.add(id)
    const r = byId.get(id)!
    const by: DerivedStatus['by'] = []
    const lapsed: DerivedStatus['lapsed'] = []
    for (const l of incoming.get(id) ?? []) {
      const entry = { effect: l.effect, resolutionId: l.resolutionId, number: byId.get(l.resolutionId)!.number }
      if (standing(l.resolutionId)) by.push(entry)
      else lapsed.push(entry)
    }
    const has = (e: ResolutionLinkEffect) => by.some((b) => b.effect === e)
    const status: ResolutionStatus = has('rescinds') ? 'rescinded' : has('supersedes') ? 'superseded' : r.expiresOn && r.expiresOn < today ? 'expired' : has('amends') ? 'amended' : 'in_effect'
    visiting.delete(id)
    const out = { status, by, lapsed }
    memo.set(id, out)
    return out
  }
  for (const r of resolutions) evaluate(r.id)
  return memo
}

export function statusSentence(d: DerivedStatus, reportedOn?: string | null, ratification?: Ratification | null, recommendation?: BoardRecommendation | null): string {
  const list = (e: ResolutionLinkEffect) => d.by.filter((b) => b.effect === e).map((b) => b.number)
  // ADR 0067: an Executive Committee action subject to the board's
  // ratification leads with where it stands with the board; ADR 0069: so
  // does a recommendation to the board.
  const standing = d.status !== 'rescinded' && d.status !== 'superseded'
  const lead = ratification && standing ? ratificationLabel(ratification) : recommendation && standing ? recommendationLabel(recommendation) : null
  const parts: string[] = [lead ?? RESOLUTION_STATUS_LABELS[d.status]]
  for (const e of ['rescinds', 'supersedes', 'amends', 'ratifies'] as const) {
    if (lead && e === 'ratifies') continue
    const n = list(e)
    if (n.length > 0) parts.push(`${LINK_EFFECT_PAST[e].toLowerCase()} ${n.join(', ')}`)
  }
  if (reportedOn) parts.push(`reported to the board ${reportedOn}`)
  return parts.length === 1 ? parts[0]! : `${parts[0]} · ${parts.slice(1).join(' · ')}`
}

// ------------------------------------------- ratification (ADR 0067)

// An Executive Committee action is reported to the board by default (it
// stands when adopted). One marked "Subject to the board's ratification"
// is not the board's act until the board ratifies it: it waits for the
// board, is ratified by an R- resolution, or is not ratified (the board's
// ratification motion failed). Derived, like the status; the EC- record
// never changes.
export const RATIFICATION_STATES = ['awaiting', 'ratified', 'not_ratified'] as const
export type RatificationState = (typeof RATIFICATION_STATES)[number]
export type Ratification = { state: RatificationState; by: { id: string; number: string } | null }
export const SUBJECT_TO_RATIFICATION_LABEL = 'Subject to the board’s ratification'
export function ratificationLabel(r: Ratification): string {
  return r.state === 'ratified' ? `Ratified by ${r.by?.number ?? 'the board'}` : r.state === 'not_ratified' ? 'Not ratified' : 'Awaiting ratification'
}

// From the links and the board's ratification motions: ratified while a
// standing R- resolution ratifies it; otherwise not ratified once a board
// motion to ratify it failed; otherwise awaiting.
export function deriveRatification(d: DerivedStatus, failedRatificationVote: boolean): Ratification {
  const by = d.by.find((b) => b.effect === 'ratifies')
  if (by) return { state: 'ratified', by: { id: by.resolutionId, number: by.number } }
  return { state: failedRatificationVote ? 'not_ratified' : 'awaiting', by: null }
}

// ------------------------------------- recommendations to the board (ADR 0069)

// An Executive Committee motion marked "Recommendation to the board"
// becomes, once adopted, a proposed board motion. Where it stands: still
// with the board, adopted by the board (as an R- resolution), or not
// adopted (the board's motion failed and none adopted it since). Derived
// from the board's motions; the EC- record never changes.
export const RECOMMENDATION_STATES = ['recommended', 'adopted', 'not_adopted'] as const
export type RecommendationState = (typeof RECOMMENDATION_STATES)[number]
export type BoardRecommendation = { state: RecommendationState; by: { id: string; number: string } | null }
export function recommendationLabel(r: BoardRecommendation): string {
  return r.state === 'adopted' ? `Adopted by the board as ${r.by?.number ?? 'a resolution'}` : r.state === 'not_adopted' ? 'Not adopted' : 'Recommended to the board'
}
export function deriveRecommendation(boardMotions: { status: string; resolution: { id: string; number: string } | null }[]): BoardRecommendation {
  const adopted = boardMotions.find((m) => m.status === 'adopted' && m.resolution)
  if (adopted) return { state: 'adopted', by: adopted.resolution }
  return { state: boardMotions.some((m) => m.status === 'failed') ? 'not_adopted' : 'recommended', by: null }
}

// Where an EC- resolution stands with the board, for a pill: its
// ratification or its recommendation, unless rescinded or superseded.
export function boardStanding(status: ResolutionStatus, ratification?: Ratification | null, recommendation?: BoardRecommendation | null): { label: string; tone: 'waiting' | 'yes' | 'no' } | null {
  if (status === 'rescinded' || status === 'superseded') return null
  if (ratification) return { label: ratificationLabel(ratification), tone: ratification.state === 'ratified' ? 'yes' : ratification.state === 'not_ratified' ? 'no' : 'waiting' }
  if (recommendation) return { label: recommendationLabel(recommendation), tone: recommendation.state === 'adopted' ? 'yes' : recommendation.state === 'not_adopted' ? 'no' : 'waiting' }
  return null
}

// Words in an Executive Committee motion that may name a power the bylaws
// or Iowa Code §504.825 keep for the full board. Hub warns, never blocks.
const RESERVED_POWER_WORDS: [RegExp, string][] = [
  [/\bby-?laws?\b/i, 'bylaws'],
  [/\barticles\b/i, 'articles'],
  [/\bdissol(?:ve|ves|ved|ving|ution)\b/i, 'dissolve'],
  [/\bmerg(?:e|es|ed|ing|er)\b/i, 'merge'],
  [/\bvacanc(?:y|ies)\b/i, 'vacancy'],
  [/\bremov(?:e|es|ed|ing|al)\b/i, 'remove'],
  [/\belect(?:s|ed|ing|ion|ions)?\b/i, 'elect'],
  [/\bdistributions?\b/i, 'distribution'],
  [/\bsubstantially all\b/i, 'substantially all'],
]
export function reservedPowerMatches(text: string): string[] {
  return RESERVED_POWER_WORDS.filter(([re]) => re.test(text)).map(([, word]) => word)
}
// ADR 0069: it suggests a recommendation to the board.
export const reservedPowerWarning = (words: string[]) => (words.length > 0 ? `May be reserved to the full board: ${words.map((w) => `“${w}”`).join(', ')}. Consider Recommendation to the board.` : null)

// -------------------------------------------------------------- topics

export const RESOLUTION_TOPICS = ['budget', 'elections', 'appointments', 'policy', 'rates and fees', 'contracts', 'real estate', 'bylaws', 'banking', 'spending', 'events', 'personnel', 'insurance', 'other'] as const
export const TopicSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(2)
  .max(40)
  .regex(/^[a-z0-9][a-z0-9 &'-]*$/, 'Letters, numbers, spaces, &, apostrophes and hyphens')

// -------------------------------------------------- certified copies

// What the Secretary certifies: the resolution exactly as recorded. The
// hash covers these fields in this order, so a certified copy can be
// checked against the register.
export function certificationContent(r: { number: string; title: string; text: string; adoptedOn: string; body: 'board' | 'executive' }) {
  return [`number:${r.number}`, `body:${r.body}`, `adopted_on:${r.adoptedOn}`, `title:${r.title}`, `text:${r.text}`].join('\n')
}

// ------------------------------------------------------------- shapes

export type ResolutionListItem = {
  id: string
  number: string
  series: ResolutionSeries
  fiscalYear: number
  adoptedOn: string
  title: string
  executive: boolean
  redacted: boolean
  body: 'board' | 'executive'
  origin: 'hub' | 'records'
  status: ResolutionStatus
  statusText: string
  // A board resolution that lets the Executive Committee amend or rescind it.
  authorizesExecutive: boolean
  // ADR 0067: an EC- resolution adopted subject to the board's
  // ratification, and where it stands; null for every other resolution.
  ratification: Ratification | null
  // ADR 0069: an EC- resolution adopted as a recommendation to the board,
  // and where it stands; null for every other resolution.
  recommendation: BoardRecommendation | null
  topics: string[]
  meetingId: string | null
  meetingTitle: string | null
  recordMeetingId: string | null
  // A short piece of the text around a search match.
  snippet: string | null
}

export type ResolutionDetail = ResolutionListItem & {
  text: string | null
  source: 'meeting' | 'email_meeting' | 'written_consent'
  motionId: string | null
  emailMeetingId: string | null
  agendaItem: { id: string; title: string } | null
  documents: { id: string; title: string }[]
  minutes: { id: string; status: string; meetingTitle: string } | null
  mover: PersonRef | null
  seconder: PersonRef | null
  moverAsWritten: string | null
  seconderAsWritten: string | null
  threshold: string | null
  tally: VoteTally | null
  voteText: string | null
  votes: { person: PersonRef; choice: string }[]
  recusals: PersonRef[]
  authorizesExecutive: boolean
  expiresOn: string | null
  derived: DerivedStatus
  reported: { meetingId: string | null; meetingTitle: string | null; reportedOn: string } | null
  // Links this resolution made (it amends ...), and links to it (amended by ...).
  acts: { id: string; effect: ResolutionLinkEffect; target: { id: string; number: string; title: string }; origin: 'adoption' | 'records'; retracted: boolean }[]
  actedOnBy: { id: string; effect: ResolutionLinkEffect; by: { id: string; number: string; title: string }; origin: 'adoption' | 'records'; retracted: boolean; lapsed: boolean; lapsedBy: { id: string; number: string; effect: ResolutionLinkEffect } | null }[]
  policies: { id: string; policyId: string; policyTitle: string; effect: PolicyEffect; origin: 'adoption' | 'staff' }[]
  certifications: CertificationView[]
  record: { meetingId: string; meetingTitle: string; heldOn: string; sourceFile: string; sourceRef: string | null; bylawsEra: string; enteredBy: string | null; importItemId: string } | null
}

export type CertificationView = {
  id: string
  resolutionId: string
  number: string
  status: 'pending' | 'certified' | 'canceled'
  requestedAt: string
  requestedBy: string | null
  purpose: string | null
  signedBy: PersonRef | null
  signedAt: string | null
  channel: 'mybre' | 'attested' | null
  attestation: string | null
  hash: string | null
  // The record still matches what was certified.
  hashMatches: boolean | null
}

export type ResolutionLinkTarget = { id: string; number: string; title: string; body: 'board' | 'executive'; authorizesExecutive: boolean }

// ------------------------------------------- entered from records (§6)

// The minutes import (ADR 0048 §7) was retired on 2026-10-26. What it
// entered stays and is read with these types.

export const IMPORT_RESULTS = ['adopted', 'failed', 'tabled', 'withdrawn', 'not_recorded'] as const
export type ImportResult = (typeof IMPORT_RESULTS)[number]
export const IMPORT_RESULT_LABELS: Record<ImportResult, string> = { adopted: 'Adopted', failed: 'Failed', tabled: 'Tabled', withdrawn: 'Withdrawn', not_recorded: 'Result not recorded' }

export type ImportCandidate = { personId: string; name: string }
export type ImportAttendee = { name: string; status: 'present' | 'absent' | 'guest' | 'staff'; candidates: ImportCandidate[] }
// A candidate as review shows it (ADR 0049): whether Hub's terms and
// offices make them a voting member of the meeting's body on its date (a
// director for the board; an officer for the Executive Committee), and
// whether the minutes list the name among staff. Computed when shown, so
// past service entered later counts.
export type ImportCandidateView = ImportCandidate & { voting: boolean; staff: boolean }
export type ImportAttendeeView = Omit<ImportAttendee, 'candidates'> & { candidates: ImportCandidateView[]; note: string | null }
export type ImportPersonView = { asWritten: string; candidates: ImportCandidateView[]; personId: string | null; warning: string | null }


export type RecordMeetingView = {
  id: string
  key: string
  body: 'board' | 'executive'
  kind: 'regular' | 'special' | 'annual' | 'email_vote'
  heldOn: string
  fiscalYear: number
  title: string
  organizationName: 'AMF' | 'BRE'
  bylawsEra: string
  quorum: { status: 'met' | 'not_met' | 'not_recorded'; note: string | null }
  attendance: ImportAttendeeView[]
  executiveSession: boolean
  source: { file: string; ref: string | null; alternates: string[] }
  itemsWithheld: number
  // Attendance from a spreadsheet, applied as a supplement (ADR 0056).
  sheetAttendance?: SheetAttendanceNote[]
}

export type ImportItemView = {
  id: string
  key: string
  meetingId: string
  position: number
  type: 'motion' | 'recorded_direction'
  kind: RegisterKind
  title: string
  text: string | null
  mover: ImportPersonView | null
  seconder: ImportPersonView | null
  result: ImportResult
  vote: { yes?: number; no?: number; abstain?: number; text?: string } | null
  executive: boolean
  redacted: boolean
  topics: string[]
  links: { effect: ResolutionLinkEffect; target: string; targetItemKey?: string; suggested: ResolutionLinkTarget[] }[]
  source: { ref: string; excerpt: string | null }
  note: string | null
  status: 'pending' | 'confirmed' | 'skipped'
  skipReason: string | null
  decidedAt: string | null
  decidedBy: string | null
  resolution: { id: string; number: string } | null
  edited: boolean
  version: number
  // What confirming it would do now.
  outcome: 'numbered' | 'recorded' | 'direction'
  wouldNumber: { series: ResolutionSeries; fiscalYear: number } | null
  // An earlier item of the same series and year is still undecided: decide
  // that one first, so numbers follow the dates.
  blockedBy: { id: string; title: string; heldOn: string } | null
}

export const confirmOutcome = (it: { type: 'motion' | 'recorded_direction'; kind: RegisterKind; result: ImportResult }): ImportItemView['outcome'] =>
  it.type === 'recorded_direction' ? 'direction' : it.kind === 'decision' && it.result === 'adopted' ? 'numbered' : 'recorded'

export const importFiscalYear = (heldOn: string) => governanceFiscalYear(heldOn)

export type MeetingBodyResolution = Extract<MeetingBody, 'board' | 'executive'>
